/**
 * withDeadline.ts — nothing the chat waits on may wait forever (2026-09-28,
 * Malaika's report: "picking up right where you left off…" that never ended,
 * and a send that sat 38 s with no network call at all).
 *
 * A Firestore read queued behind a failed SDK queue, an IndexedDB open held up
 * by another tab, a token refresh on a dead connection: each is a promise that
 * can simply never settle, and every caller that awaited one waited with it.
 * withDeadline races such a promise against a timer and rejects with a
 * DeadlineError when the timer wins, so the caller's existing error path (a
 * retry row, the last confirmed workspace, a failure card) runs instead of
 * nothing. The original promise is left to settle on its own; its result is
 * ignored once the deadline has passed. The timer is always cleared.
 */

export class DeadlineError extends Error {
  readonly code = 'deadline-exceeded';
  constructor(readonly label: string, readonly ms: number) {
    super(`${label} did not finish within ${ms} ms`);
    this.name = 'DeadlineError';
    // Keeps `instanceof` true where a build compiles classes down to functions.
    Object.setPrototypeOf(this, DeadlineError.prototype);
  }
}

export function isDeadlineError(err: unknown): err is DeadlineError {
  return err instanceof DeadlineError
    || (typeof err === 'object' && err !== null && (err as { code?: unknown }).code === 'deadline-exceeded');
}

/** Settle with `promise`, or reject with a DeadlineError after `ms`. */
export function withDeadline<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new DeadlineError(label, ms)), ms);
  });
  // A rejection of the original after the deadline must not be reported as unhandled.
  promise.catch(() => undefined);
  return Promise.race([promise, deadline]).finally(() => {
    if (timer !== undefined) clearTimeout(timer);
  });
}

/** Settle with `promise`, or with `fallback` after `ms` (for reads whose absence is an answer). */
export function withDeadlineOr<T, F>(promise: Promise<T>, ms: number, fallback: F): Promise<T | F> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<F>((resolve) => {
    timer = setTimeout(() => resolve(fallback), ms);
  });
  promise.catch(() => undefined);
  return Promise.race([promise, deadline]).finally(() => {
    if (timer !== undefined) clearTimeout(timer);
  });
}
